import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { formatClassName, pickDataAttributes  } from '../../Util';
import { kebabCase } from 'lodash';
/**
 * React Toggle Checkbox components
 * @param {object}  props.defaultChecked   Initial checked state - can be overriden
 * @param {object}  props.checked          Uses onChange to override default behaviour
 * @param {string}  props.className        Layout design level customization - size: sm, lg, xl or round
 *
 * @example <caption>Usage: </caption>
 * //Default values
 * <ToggleCheckbox
 *    defaultChecked={true}
 *    size="sm"
 * />
 *
 * //Round large toggle checkbox with checked value override
 * <ToggleCheckbox
 *    checked={this.state.isChecked}
 *    onChange={() => this.setState({ isChecked: !this.state.isChecked} )} //Bad setstate example
 *    size="lg"
 * />
 */
export default class ToggleCheckbox extends Component {
  static propTypes = {
    checked: PropTypes.bool.isRequired,
    className: PropTypes.string,
    containerProps: PropTypes.object,
    defaultChecked: PropTypes.bool,
    disabled: PropTypes.bool,
    id: PropTypes.string,
    label: PropTypes.string,
    leftToggle: PropTypes.bool,
    hintText: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    size: PropTypes.oneOf(['sm', 'lg'])
  };

  static defaultProps = {
    className: '',
    leftToggle: false,
    size: 'sm'
  };

  constructor(props) {
    super(props);

    this.previouslyChecked = !!(props.checked || props.defaultChecked);
    this.state = {
      checked: !!(props.checked || props.defaultChecked),
      hasFocus: false
    };
  }

  componentWillReceiveProps(nextProps) {
    if ('checked' in nextProps) {
      this.setState({ checked: !!nextProps.checked });
    }
  }

  /**
   * Click event handler for toggle checkbox
   * @param {object} event  Event
   */
  handleClick = event => {
    const checkbox = this.input;

    if (event.target !== checkbox) {
      this.previouslyChecked = checkbox.checked;
      event.preventDefault();
      checkbox.focus();
      checkbox.click();
      return;
    }

    const checked = this.props.hasOwnProperty('checked')
      ? this.props.checked
      : checkbox.checked;

    this.setState({ checked });
  };

  /**
   * Input focus event handler
   * @param {object} event
   */
  handleFocus = (event) => {
    const { onFocus } = this.props;

    if (onFocus) {
      onFocus(event);
    }

    this.setState({ hasFocus: true });
  }

  /**
   * Input blur event handler
   * @param {object} event
   */
  handleBlur = (event) => {
    const { onBlur } = this.props;

    if (onBlur) {
      onBlur(event);
    }

    this.setState({ hasFocus: false });
  }

  render() {
    const { className, containerProps, disabled, hintText, label, leftToggle, size, ...inputProps } = this.props;
    const dataAttributes = pickDataAttributes(this.props, 'toggle-checkbox');
    if(!dataAttributes['data-action']){
      dataAttributes['data-action'] = label ? `toggle-${kebabCase(label)}` : 'toggle';
    }

    const classes = formatClassName([
      className,
      'round',
      size
    ]);

    const labelClasses = formatClassName([
      'toggle-checkbox-label',
      leftToggle ? 'm-l-10' : 'm-r-10'
    ])
    const toggle = (
      <div className={`toggle--checkbox ${classes} ${disabled && 'disabled'}`} onClick={this.handleClick} {...dataAttributes}>
        <input
          {...inputProps}
          disabled={disabled}
          className="toggle--check-input"
          ref={ref => {
            this.input = ref;
          }}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          type="checkbox"
          value={true}
        />
        <span className="toggle--slider" />
        {/* if we have a named input we want to add the value to the form */}
        {inputProps.name && <input type="hidden" name={inputProps.name} value={!!this.state.checked} />}
      </div>
    );
    if (!label) {
      return toggle;
    }
    return (
      <>
        <div className="toggle-checkbox-container" {...containerProps}>
          {leftToggle && toggle}
          <label className={labelClasses} htmlFor={this.props.id}>
            {label}
          </label>
          {!leftToggle && toggle}
        </div>
        {hintText && <div className="toggle-checkbox-hint-text">{hintText}</div>}
      </>
    );
  }
}
